import { useState, useEffect, useCallback } from 'react';

/**
 * 🔒 Premium Radius Hook
 * 50km 유료 반경 기능을 관리하는 독립 Hook
 * 
 * LocalStorage 기반 상태 관리:
 * - premiumRadiusExpiry: 만료 시간 (timestamp)
 */

const STORAGE_KEY = 'premium_radius_expiry';
const PREMIUM_DURATION = 24 * 60 * 60 * 1000; // 24 hours
export const PREMIUM_COST = 100; // STAR

export const usePremiumRadius = () => {
    const [isUnlocked, setIsUnlocked] = useState(false);
    const [expiryTime, setExpiryTime] = useState(null);
    const [timeLeft, setTimeLeft] = useState('');

    // 🎯 만료 시간 체크 및 상태 초기화
    const checkStatus = useCallback(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) {
            setIsUnlocked(false);
            setExpiryTime(null);
            return false;
        }

        const expiry = parseInt(stored, 10);
        const now = Date.now();

        if (now >= expiry) {
            // 만료됨
            localStorage.removeItem(STORAGE_KEY);
            setIsUnlocked(false);
            setExpiryTime(null);
            return false;
        }

        // 유효함
        setIsUnlocked(true);
        setExpiryTime(expiry);
        return true;
    }, []);

    // 🎯 Mount 시 상태 복원
    useEffect(() => {
        checkStatus();
    }, [checkStatus]);

    // 🎯 타이머 업데이트 (1초마다)
    useEffect(() => {
        if (!isUnlocked || !expiryTime) {
            setTimeLeft('');
            return;
        }

        const updateTimer = () => {
            const now = Date.now();
            const diff = expiryTime - now;

            if (diff <= 0) {
                checkStatus(); // 만료 처리
                return;
            }

            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            setTimeLeft(`${hours}:${minutes.toString().padStart(2, '0')}`);
        };

        updateTimer(); // 즉시 1회 실행
        const interval = setInterval(updateTimer, 1000);

        return () => clearInterval(interval);
    }, [isUnlocked, expiryTime, checkStatus]);

    // 🎯 해금 함수
    const unlock = useCallback(() => {
        const expiry = Date.now() + PREMIUM_DURATION;
        localStorage.setItem(STORAGE_KEY, expiry.toString());
        setExpiryTime(expiry);
        setIsUnlocked(true);
    }, []);

    // 🎯 강제 잠금 (테스트용)
    const lock = useCallback(() => {
        localStorage.removeItem(STORAGE_KEY);
        setIsUnlocked(false);
        setExpiryTime(null);
        setTimeLeft('');
    }, []);

    return {
        isUnlocked,
        timeLeft,
        unlock,
        lock, // 테스트용
        checkStatus
    };
};
